import { For, createMemo, type JSX } from 'solid-js';
import HexCell from './HexCell';

// The Molecular Hive is the signature visualization of Terp Track.
//
// Design rules (PRD §4, mockups/Mobile View, Strain Detail):
//  - Uniform hexagonal cells in the brand-hex green.
//  - Ordered by concentration, highest first.
//  - Horizontally scrollable with "peek-off" at the edge so users learn
//    they can swipe past the visible 5 cells.
//  - Accessible keyboard nav via native scroll-snap + tab order on cells.

export type HiveTerpene = {
  slug: string;
  label: string;
  percentage: number;
  activationTempC?: number;
};

export type MolecularHiveProps = {
  terpenes: HiveTerpene[];
  activeSlug?: string;
  // When true, cells wrap onto the next line instead of overflowing
  // horizontally. Used on the Strain Detail page so the full profile is
  // visible without swiping. The home-page teaser keeps `wrap=false` for
  // the peek-off effect.
  wrap?: boolean;
};

export default function MolecularHive(props: MolecularHiveProps): JSX.Element {
  const ordered = createMemo(() =>
    [...props.terpenes].sort((a, b) => b.percentage - a.percentage),
  );

  return (
    <div
      class="relative"
      style={
        props.wrap
          ? {}
          : {
              '-webkit-mask-image':
                'linear-gradient(to right, black 0, black calc(100% - 3rem), transparent 100%)',
              'mask-image':
                'linear-gradient(to right, black 0, black calc(100% - 3rem), transparent 100%)',
            }
      }
    >
      <div
        class={
          props.wrap
            ? 'flex flex-wrap gap-3 pb-2'
            : 'flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2'
        }
        role="list"
        aria-label="Terpene profile, sorted by concentration"
      >
        <For each={ordered()}>
          {(t) => (
            <div role="listitem" class="snap-start">
              <HexCell
                label={t.label}
                percentage={t.percentage}
                tone={props.activeSlug === t.slug ? 'active' : 'default'}
                title={
                  t.activationTempC
                    ? `${t.label}: activate ≥ ${t.activationTempC}°C`
                    : t.label
                }
              />
            </div>
          )}
        </For>
      </div>
    </div>
  );
}
